import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { AuthRequired } from "../components/AuthRequired";

// Money Operations landing — a guided index that separates the four
// money-adjacent operator surfaces so the destination of funds is never
// ambiguous. This page moves no funds and creates no proposals; it only
// routes the operator to the correct queue with an explicit "where the
// money goes" label up front.
//
// The single most important distinction this page enforces visually:
//   * Treasury withdrawal pays the TREASURY wallet (house funds).
//   * Affiliate payout pays the partner USER's destination address.
// Conflating the two is the costly mistake; the cards below are colour-
// and copy-separated to prevent it.

type Tone = "treasury" | "affiliate" | "partner" | "credit";

interface FlowCard {
  tone: Tone;
  title: string;
  to: string;
  whereMoneyGoes: ReactNode;
  cta: string;
  movesFunds: boolean;
}

// Accent, and the SURFACE PAIR that goes with it.
//
// This map previously held bare accents and the CTA rendered `color: "white"`
// on `background: accent`. That worked while the accents were the old dark
// literals. It stopped working when the token layer repointed `--fg` from
// #333-ish to #e9edf3: the partner card's CTA became white text on a
// near-white ground — unreadable, and the one contrast regression the token
// repoint actually caused. Solid fills are replaced by the console's tinted
// treatment, which is legible at every tone and matches the rest of the app.
const TONE_ACCENT: Record<Tone, string> = {
  treasury: "var(--danger)",
  affiliate: "var(--accent)",
  partner: "var(--fg-muted)",
  credit: "var(--ok)",
};

const TONE_TINT: Record<Tone, string> = {
  treasury: "var(--danger-bg)",
  affiliate: "var(--accent-bg)",
  partner: "var(--neutral-bg)",
  credit: "var(--ok-bg)",
};

const TONE_EDGE: Record<Tone, string> = {
  treasury: "var(--danger-border)",
  affiliate: "var(--accent-border)",
  partner: "var(--neutral-border)",
  credit: "var(--ok-border)",
};

const CARDS: ReadonlyArray<FlowCard> = [
  {
    tone: "treasury",
    title: "Treasury Withdraw",
    to: "/escrow/withdraw-treasury",
    movesFunds: true,
    whereMoneyGoes: "Pays the TREASURY wallet (house funds) — not an affiliate user.",
    cta: "Open Treasury withdraw",
  },
  {
    tone: "affiliate",
    title: "Affiliate Payouts",
    to: "/admin/affiliate/withdrawals",
    movesFunds: true,
    whereMoneyGoes: "Pays an approved partner withdrawal to the USER's destination.",
    cta: "Open payouts queue",
  },
  {
    tone: "partner",
    title: "Partner Applications",
    to: "/admin/affiliate/partners",
    movesFunds: false,
    whereMoneyGoes: "No funds move. Approve / reject / suspend partner status.",
    cta: "Open applications",
  },
  {
    tone: "credit",
    title: "Credit Redemptions",
    to: "/admin/affiliate/redemptions",
    movesFunds: false,
    whereMoneyGoes: "No cash, no on-chain tx. Applies in-ecosystem CREDITS — not Partner Cash.",
    cta: "Open redemptions",
  },
];

function Card({ card }: { card: FlowCard }) {
  const accent = TONE_ACCENT[card.tone];
  const tint = TONE_TINT[card.tone];
  const edge = TONE_EDGE[card.tone];
  return (
    <div
      data-flow={card.tone}
      style={{
        display: "flex",
        flexDirection: "column",
        color: "var(--fg)",
        border: "1px solid var(--border)",
        borderLeft: `4px solid ${accent}`,
        borderRadius: 8,
        padding: "14px 16px",
        background: "var(--bg-card)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, color: accent }}>{card.title}</h3>
        <span
          style={{
            marginLeft: "auto",
            fontSize: 10,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.4,
            padding: "2px 8px",
            borderRadius: 999,
            color: card.movesFunds ? "var(--danger, #d23f3f)" : "var(--fg-dim)",
            border: `1px solid ${card.movesFunds ? "var(--danger, #d23f3f)" : "var(--border)"}`,
          }}
        >
          {card.movesFunds ? "Moves funds" : "No funds"}
        </span>
      </div>
      <p
        role="note"
        style={{ margin: "8px 0 12px 0", fontSize: 12.5, color: "var(--fg-dim)", lineHeight: 1.4 }}
      >
        {card.whereMoneyGoes}
      </p>
      <Link
        to={card.to}
        style={{
          marginTop: "auto",
          textAlign: "center",
          padding: "8px 14px",
          fontSize: 13,
          fontWeight: 640,
          borderRadius: "var(--r-sm)",
          textDecoration: "none",
          color: accent,
          background: tint,
          border: `1px solid ${edge}`,
          // 44px+ minimum target for a major action.
          minHeight: 44,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {card.cta} →
      </Link>
    </div>
  );
}

export function MoneyOperations() {
  return (
    <div>
      <PageHeader
        title="Money Operations"
        subtitle="Pick a flow. Nothing here moves funds — each card opens its guided queue."
      />
      <AuthRequired>
        <div
          role="alert"
          style={{
            margin: "8px 0 16px 0",
            padding: "10px 12px",
            border: "1px solid var(--danger, #d23f3f)",
            background: "rgba(210, 63, 63, 0.06)",
            color: "var(--danger, #d23f3f)",
            borderRadius: 4,
            fontSize: 12.5,
          }}
        >
          <strong>Treasury ≠ Affiliate.</strong> Treasury pays the treasury wallet
          (house funds); Affiliate pays the partner user. Confirm before acting.
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 12,
          }}
        >
          {CARDS.map((c) => (
            <Card key={c.to} card={c} />
          ))}
        </div>
      </AuthRequired>
    </div>
  );
}
